import React, { useCallback, useEffect, useId, useState } from 'react';
import { BookOpen, FileText, Loader2, Search, Trash2, Upload } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import { useAuthStore } from '@/stores/authStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  apiClient,
  KbDocument,
  KbIngestBlockedError,
  KbScope,
  KbSearchHit,
} from '@/utils/apiClient';

/** The backend refuses a document over 500 000 characters; a file is read
 * client-side, so the same bound (in KB) is checked before it is sent. */
const MAX_FILE_KB = 500;
const ACCEPTED = ['.txt', '.md', '.log'];

type Mode = 'paste' | 'file';

const card =
  'rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5';
const label = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const input =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500';
const primary =
  'inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500';

function fill(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (out, [k, v]) => out.split(`{${k}}`).join(String(v)),
    template,
  );
}

/**
 * STORY-303 — the knowledge-base tab: list, add (paste or a text file), try
 * the search and delete, per the backend's role rules. Shared documents are
 * an admin's to add and delete; anyone may keep personal ones, which only
 * they see. A blocked upload shows the KINDS of what was found — the backend
 * never sends the content back, and neither does this screen.
 */
export const KnowledgeBaseSettings: React.FC = () => {
  const { t } = useTranslation();
  const k = t.knowledge;
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const ids = useId();

  const [docs, setDocs] = useState<KbDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [mode, setMode] = useState<Mode>('paste');
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [scope, setScope] = useState<KbScope>(isAdmin ? 'shared' : 'personal');
  const [uploading, setUploading] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<KbDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [hits, setHits] = useState<KbSearchHit[] | null>(null);

  const kindsText = (kinds: string[]) =>
    kinds.map((kind) => (k.kinds as Record<string, string>)[kind] || kind).join(', ');
  const failure = (err: unknown) =>
    fill(k.error, { message: err instanceof Error ? err.message : String(err) });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDocs(await apiClient.listKbDocuments());
    } catch (err) {
      setError(failure(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The profile can arrive after the first render; an admin starts on shared.
  useEffect(() => {
    setScope(isAdmin ? 'shared' : 'personal');
  }, [isAdmin]);

  const effectiveScope: KbScope = isAdmin ? scope : 'personal';
  const canDelete = (d: KbDocument) => (d.scope === 'personal' ? d.own : isAdmin);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setError(null);
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!ACCEPTED.some((ext) => lower.endsWith(ext))) {
      setError(k.fileUnsupported);
      return;
    }
    if (file.size > MAX_FILE_KB * 1024) {
      setError(fill(k.fileTooLarge, { limit: MAX_FILE_KB }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setContent(typeof reader.result === 'string' ? reader.result : '');
      setFileName(file.name);
      if (!title.trim()) setTitle(file.name.replace(/\.[^.]+$/, ''));
    };
    reader.readAsText(file);
  };

  const onUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    setUploading(true);
    setError(null);
    setNotice(null);
    try {
      const res = await apiClient.ingestKbDocument({
        title: title.trim(),
        content,
        source: mode === 'file' && fileName ? fileName : 'upload',
        scope: effectiveScope,
      });
      let msg = fill(k.uploaded, { title: res.title, chunks: res.chunks });
      if (res.redacted_kinds?.length) {
        msg += ' ' + fill(k.redactedNotice, { kinds: kindsText(res.redacted_kinds) });
      }
      setNotice(msg);
      setTitle('');
      setContent('');
      setFileName(null);
      await load();
    } catch (err) {
      if (err instanceof KbIngestBlockedError) {
        setError(fill(k.blocked, { kinds: kindsText(err.findings) }));
      } else {
        setError(failure(err));
      }
    } finally {
      setUploading(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await apiClient.deleteKbDocument(pendingDelete.id);
      setNotice(k.deleted);
      setPendingDelete(null);
      await load();
    } catch (err) {
      setError(failure(err));
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const onSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setError(null);
    try {
      setHits(await apiClient.searchKb(query.trim(), 5));
    } catch (err) {
      setError(failure(err));
    } finally {
      setSearching(false);
    }
  };

  const modeButton = (value: Mode, text: string, Icon: React.FC<{ className?: string }>) => (
    <button
      type="button"
      aria-pressed={mode === value}
      onClick={() => setMode(value)}
      className={clsx(
        'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500',
        mode === value
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300',
      )}
    >
      <Icon className="h-4 w-4" />
      {text}
    </button>
  );

  return (
    <div className="space-y-6 max-w-4xl">
      <header>
        <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white">
          <BookOpen className="h-5 w-5" aria-hidden="true" />
          {k.title}
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{k.subtitle}</p>
      </header>

      {error && (
        <div role="alert" data-testid="kb-error"
             className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" data-testid="kb-notice"
             className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300">
          {notice}
        </div>
      )}

      <section className={card} aria-labelledby={`${ids}-upload`}>
        <h3 id={`${ids}-upload`} className="mb-4 text-base font-semibold text-gray-900 dark:text-white">
          {k.uploadTitle}
        </h3>
        <form onSubmit={onUpload} className="space-y-4">
          <div>
            <label htmlFor={`${ids}-title`} className={label}>{k.titleLabel}</label>
            <input id={`${ids}-title`} type="text" className={input} value={title} maxLength={500}
                   onChange={(e) => setTitle(e.target.value)} required />
          </div>

          <div role="group" aria-label={k.sourceMode} className="flex gap-2">
            {modeButton('paste', k.modePaste, FileText)}
            {modeButton('file', k.modeFile, Upload)}
          </div>

          {mode === 'paste' ? (
            <div>
              <label htmlFor={`${ids}-content`} className={label}>{k.contentLabel}</label>
              <textarea id={`${ids}-content`} className={clsx(input, 'min-h-[10rem] font-mono')}
                        value={content} placeholder={k.contentPlaceholder}
                        onChange={(e) => setContent(e.target.value)} />
            </div>
          ) : (
            <div>
              <label htmlFor={`${ids}-file`} className={label}>{k.fileLabel}</label>
              <input id={`${ids}-file`} type="file" accept={ACCEPTED.join(',')} onChange={onFile}
                     className="block text-sm text-gray-700 dark:text-gray-300" />
              {fileName && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {fill(k.fileChosen, { name: fileName })}
                </p>
              )}
            </div>
          )}

          {isAdmin ? (
            <fieldset>
              <legend className={label}>{k.scopeLabel}</legend>
              <div className="flex flex-col gap-1 sm:flex-row sm:gap-6">
                {(['shared', 'personal'] as KbScope[]).map((value) => (
                  <label key={value} className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input type="radio" name={`${ids}-scope`} value={value} checked={scope === value}
                           onChange={() => setScope(value)} />
                    {value === 'shared' ? k.scopeSharedHint : k.scopePersonalHint}
                  </label>
                ))}
              </div>
            </fieldset>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="kb-personal-note">
              {k.personalOnlyNote}
            </p>
          )}

          <button type="submit" className={primary}
                  disabled={uploading || !title.trim() || !content.trim()}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? k.uploading : k.upload}
          </button>
        </form>
      </section>

      <section className={card} aria-labelledby={`${ids}-list`}>
        <h3 id={`${ids}-list`} className="mb-4 text-base font-semibold text-gray-900 dark:text-white">
          {k.listTitle}
        </h3>
        {loading ? (
          <p className="text-sm text-gray-500">{k.loading}</p>
        ) : docs.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="kb-empty">{k.empty}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="kb-table">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th scope="col" className="py-2 pr-4 font-medium">{k.colTitle}</th>
                  <th scope="col" className="py-2 pr-4 font-medium">{k.colScope}</th>
                  <th scope="col" className="py-2 pr-4 font-medium">{k.colCluster}</th>
                  <th scope="col" className="py-2 pr-4 font-medium">{k.colChunks}</th>
                  <th scope="col" className="py-2 pr-4 font-medium">{k.colDate}</th>
                  <th scope="col" className="py-2 font-medium"><span className="sr-only">{k.colActions}</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {docs.map((d) => (
                  <tr key={d.id} className="text-gray-800 dark:text-gray-200">
                    <td className="py-2 pr-4 break-words">{d.title}</td>
                    <td className="py-2 pr-4">{d.scope === 'personal' ? k.scopePersonal : k.scopeShared}</td>
                    <td className="py-2 pr-4">{d.cluster || k.allClusters}</td>
                    <td className="py-2 pr-4">{d.chunks}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2 text-right">
                      {canDelete(d) && (
                        <button type="button" onClick={() => setPendingDelete(d)}
                                aria-label={fill(k.deleteLabel, { title: d.title })}
                                className="rounded p-1 text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 dark:hover:bg-red-900/30">
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={card} aria-labelledby={`${ids}-search`}>
        <h3 id={`${ids}-search`} className="mb-1 text-base font-semibold text-gray-900 dark:text-white">
          {k.searchTitle}
        </h3>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">{k.searchHint}</p>
        <form onSubmit={onSearch} className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label htmlFor={`${ids}-q`} className={label}>{k.searchLabel}</label>
            <input id={`${ids}-q`} type="search" className={input} value={query}
                   onChange={(e) => setQuery(e.target.value)} />
          </div>
          <button type="submit" className={primary} disabled={searching || !query.trim()}>
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {k.searchButton}
          </button>
        </form>
        {hits !== null && (
          hits.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500" data-testid="kb-search-none">{k.searchNone}</p>
          ) : (
            <ol className="mt-4 space-y-3" data-testid="kb-search-hits">
              {hits.map((h) => (
                <li key={`${h.document_id}-${h.seq}`} className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {h.title} · #{h.seq}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">{h.content}</p>
                </li>
              ))}
            </ol>
          )
        )}
      </section>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title={k.deleteTitle}
        message={pendingDelete ? fill(k.deleteConfirm, { title: pendingDelete.title }) : undefined}
        confirmLabel={t.common.delete}
        danger
        loading={deleting}
        onConfirm={onConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};
